import { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';

// Import all locale files
import es from '../locales/es.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';

const locales = { es, en, de, fr, it };

/**
 * Hook for accessing translations in the current language
 * @returns {Object} Translation object with all keys
 */
export const useTranslations = () => {
  const { language } = useLanguage();
  
  const t = useMemo(() => {
    return locales[language] || locales.es;
  }, [language]);
  
  return t;
};

/**
 * Get a nested translation value by path
 * @param {Object} obj - Translation object
 * @param {string} path - Dot-separated path (e.g., 'common.save')
 * @param {Object} params - Optional parameters for string interpolation
 * @returns {string} Translated string
 */
export const getNestedTranslation = (obj, path, params = {}) => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return path; // Return the path if translation not found
    }
  }
  
  // Handle string interpolation
  if (typeof result === 'string' && Object.keys(params).length > 0) {
    return result.replace(/\{(\w+)\}/g, (_, key) => params[key] || `{${key}}`);
  }
  
  return result;
};

/**
 * Hook for accessing translations with a getter function
 * Useful for dynamic translation keys
 * @returns {Object} Object with language, t (translations), and get function
 */
export const useTranslationsWithGetter = () => {
  const { language } = useLanguage();
  
  const translations = useMemo(() => {
    return locales[language] || locales.es;
  }, [language]);
  
  const get = (path, params = {}) => {
    return getNestedTranslation(translations, path, params);
  };
  
  return {
    language,
    t: translations,
    get,
  };
};

/**
 * Get all translations for a specific section
 * @param {string} section - Section name (e.g., 'common', 'auth', 'products')
 * @returns {Object} Section translations
 */
export const useTranslationSection = (section) => {
  const { language } = useLanguage();
  
  return useMemo(() => {
    const locale = locales[language] || locales.es;
    return locale[section] || {};
  }, [language, section]);
};

// Export locales for direct access if needed
export { locales };

// Language utilities
export const availableLanguages = ['es', 'en', 'de', 'fr', 'it'];

export const languageLabels = {
  es: 'Español',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
};

export const getLanguageLabel = (code) => languageLabels[code] || code;
